import React, { useState } from "react";
import { Button, Input, Separator } from "@postcheesy/ui-lib/atoms";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Form, FormControl, FormItem, FormLabel, FormMessage, Toaster, useToast } from "@postcheesy/ui-lib/molecules";
import { useForm } from "react-hook-form";
import axios from "axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Layout } from "../../components/layouts/layout";
import { Container } from "../../components/layouts/container";
import { LogoutButton } from "../../components/ui/LogoutButton";

const forgotPasswordSchema = z.object({
    email: z
        .string()
        .email("Veuillez entrer une adresse e-mail valide"),
});

export const ForgotPassword: React.FC = () => {
    const { toast } = useToast();
    const [resendCooldown, setResendCooldown] = useState(0);
    const [sentCode, setSentCode] = useState(false);
    const [isRequestPending, setIsRequestPending] = useState(false);

    const forgotPasswordForm = useForm<z.infer<typeof forgotPasswordSchema>>({
        resolver: zodResolver(forgotPasswordSchema),
        mode: "onBlur",
    });

    const handleForgotPassword = async (data: z.infer<typeof forgotPasswordSchema>) => {
        if (isRequestPending) return;

        setIsRequestPending(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            await axios.post(`${apiUrl}/forgotPassword`, { email: data.email });
            localStorage.setItem('email', data.email);
            toast({
                description: "Le code de réinitialisation a été envoyé à votre adresse e-mail.",
                variant: "default",
            });
            setSentCode(true);

        } catch (error) {
            // console.error("Erreur lors de l'envoi du code de réinitialisation:", error);
            toast({
                title: "Erreur",
                description: "Échec de l'envoi du code. Veuillez réessayer.",
                variant: "destructive",
            });
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return;

        await handleForgotPassword(forgotPasswordForm.getValues());

        setResendCooldown(15);
        const interval = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev === 1) clearInterval(interval);
                return prev - 1;
            });
        }, 1000);
    };

    return (
        <Layout>
            <Toaster />
            <Container>
                <div className="relative mx-auto grid max-h-full w-fit grid-cols-1 items-start justify-center">
                    <section className="lg:my-18 container mx-auto my-12 h-fit max-w-xl py-8 md:my-12 2xl:my-32">
                        <LogoutButton variant="link">{"< Retourner à la page de connexion"}</LogoutButton>
                        <Card className="w-full md:min-w-96">
                            <CardHeader>
                                <CardTitle>Réinitialiser votre mot de passe</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Form {...forgotPasswordForm}>
                                    <form
                                        onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}
                                        className="space-y-4"
                                    >
                                        <FormItem>
                                            <FormLabel>Email</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="email"
                                                    placeholder="Entrez votre email"
                                                    {...forgotPasswordForm.register("email")}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                        {sentCode ? (
                                            <>
                                                <Button
                                                    onClick={handleResendCode}
                                                    className="w-full"
                                                    disabled={resendCooldown > 0}
                                                    variant={'outline'}
                                                >
                                                    {resendCooldown > 0 ? `Réenvoyer le code dans ${resendCooldown} seconde(s)` : "Réenvoyer le code de réinitialisation"}
                                                </Button>
                                            </>
                                        ) :
                                            (
                                                <Button
                                                    type="submit"
                                                    className="w-full"
                                                >
                                                    Envoyer le code de réinitialisation
                                                </Button>

                                            )
                                        }

                                    </form>
                                </Form>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </Container>
        </Layout>
    );
};